import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isGameSeed } from '@orborix/shared-types';
import type { ShipState } from '../../../lib/englishApi.js';
import { useViewNavigate } from '../../../hooks/useViewNavigate.js';
import { useSfx } from '../../../lib/sfx/index.js';
import { iconSvg } from './shipArt.js';
import { pieceById, type ShipPieceId } from './pieces.js';
import { useShip } from './useShip.js';
import { antenaRemaining, pendingListenSeconds, subscribeListen } from './shipListenMeter.js';
import { ListenContext } from '../../listen/ListenContext.js';
import { ProgressTour } from './ProgressTour.js';
import { setShipAskOpen } from './shipAskOpen.js';
import './ship.css';

/** Cada cuánto se pregunta cuánto lleva la pieza (el servidor la calcula de la actividad real). */
const POLL_MS = 2500;
/** …y cuando la pantalla ya la ve cumplida y falta que el servidor la sume. */
const FAST_POLL_MS = 1000;

/**
 * Qué pieza es la charla en curso: la escena es el Tren, la lección la Computadora de rumbo, un
 * juego los Motores y la charla libre (también la de un capítulo) la Cabina. El examen y la charla
 * A0 no son de la nave.
 */
export function conversationPiece(c: {
  examMode: boolean;
  a0Mission: unknown;
  scenarioLabel: unknown;
  planLabel: unknown;
  activeSeed: string | null;
}): ShipPieceId | null {
  if (c.examMode || c.a0Mission) return null;
  if (c.scenarioLabel) return 'tren';
  if (c.planLabel) return 'rumbo';
  if (isGameSeed(c.activeSeed)) return 'motores';
  return 'cabina';
}

type PieceState = ShipState['pieces'][number];

/**
 * Lo que la pantalla sabe en vivo y el servidor todavía no (`have` de `need`): los objetivos de la
 * escena o de la lección que la charla ya tildó, o la posición del capítulo en el reproductor.
 */
export interface PieceLive {
  have: number;
  need: number;
}

/** Los objetivos en vivo de la charla: la misión de la escena o los de la lección. */
export function conversationLive(c: {
  scenarioMission: { objectives: ReadonlyArray<{ id: string; kind?: string }> } | null;
  completedObjectives: ReadonlyArray<string>;
  subUnit: unknown;
  subUnitObjectives: ReadonlyArray<unknown>;
  subUnitCompleted: ReadonlyArray<number>;
  planObjectives: ReadonlyArray<unknown>;
  completedPlanObjectives: ReadonlyArray<number>;
}): PieceLive | undefined {
  // La misión de la escena son sus objetivos PRINCIPALES: el bonus no la frena (como la charla, que
  // festeja «¡Misión cumplida!» con el bonus pendiente, y el servidor, que la suma igual).
  const primary = c.scenarioMission?.objectives.filter((o) => o.kind !== 'bonus') ?? [];
  if (primary.length > 0) {
    const met = new Set(c.completedObjectives);
    return { have: primary.filter((o) => met.has(o.id)).length, need: primary.length };
  }
  if (c.subUnit && c.subUnitObjectives.length > 0)
    return { have: c.subUnitCompleted.length, need: c.subUnitObjectives.length };
  if (c.planObjectives.length > 0)
    return { have: c.completedPlanObjectives.length, need: c.planObjectives.length };
  return undefined;
}

/** «Te falta 1 objetivo» / «Te faltan 2 objetivos». */
const faltan = (n: number, one: string, many: string) =>
  n === 1 ? `Te falta 1 ${one}` : `Te faltan ${n} ${many}`;
const clock = (sec: number) => {
  const s = Math.max(0, Math.ceil(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** «Te faltan 40 s», «Te faltan 2 objetivos de la misión», «Te falta 1 palabra»… */
export function pieceRemaining(p: PieceState, live?: PieceLive): string {
  const left = Math.max(0, p.need - p.have);
  switch (p.id) {
    case 'cabina': {
      const parts: string[] = [];
      if (left > 0) parts.push(`Te faltan ${left} s`);
      if (p.said && p.said.have < p.said.need)
        parts.push(
          `${p.said.have} de ${p.said.need} cosas dichas` +
            // En A0 lo mandado con «Inspirar» cuenta, con ayuda (Emmanuel, 22/10, card #603).
            (p.said.helped > 0 ? ` (${p.said.helped} con ayuda)` : ''),
        );
      return parts.join(' · ') || 'Casi';
    }
    case 'antena': {
      const a = live ?? { have: p.have, need: p.need };
      return `Te falta ${clock(a.need - a.have)} de escucha`;
    }
    case 'rumbo': {
      if (!live) return 'Te falta terminar la lección';
      const n = live.need - live.have;
      return n > 0
        ? `${faltan(n, 'objetivo', 'objetivos')} de la lección`
        : '¡Objetivos cumplidos! Terminá la lección para sumarla';
    }
    case 'tren': {
      const mission = live ?? (p.need > 0 ? { have: p.have, need: p.need } : undefined);
      if (!mission) return 'Cumplí la misión de una escena';
      const n = mission.need - mission.have;
      return n > 0
        ? `${faltan(n, 'objetivo', 'objetivos')} de la misión`
        : '¡Misión cumplida! Cerrá la escena para sumarla';
    }
    case 'bodega':
      // El para qué, también acá (Emmanuel, 20/10): las guardadas vuelven en «Practicar».
      return `${left === 1 ? 'Te falta 1 palabra' : `Te faltan ${left} palabras`} · después las decís en una frase`;
    case 'motores':
      return left === 1 ? 'Te falta 1 ronda' : `Te faltan ${left} rondas`;
    case 'radar':
      return 'Terminá el recorrido por las pestañas';
  }
}

/**
 * La pregunta al completar la pieza en su actividad (Emmanuel, 20/10: «por si el usuario se enganchó con
 * lo que está haciendo»): seguir o volver a la nave. `ready` = buen momento para preguntar (en la charla,
 * el coach terminó su turno; en el reproductor, el capítulo no terminó — al terminar ya pregunta la hoja).
 * `onBack` = volver como corresponde en esa pantalla (la charla se cierra como el cierre de siempre).
 */
export interface ShipAsk {
  ready: boolean;
  onBack?: (() => void) | undefined;
  /**
   * Una opción más, primera (la Bodega: «Decilas ahora (1 min)», Emmanuel 20/10): un paso corto con
   * lo que se acaba de hacer, antes de volver a la nave.
   */
  extra?: { label: string; onClick: () => void } | undefined;
}

/** Qué se pregunta y qué dice «seguir», según la pieza. El Radar no pregunta: ya cerró su recorrido. */
export const SHIP_ASK_TEXT: Record<ShipPieceId, { question: string; keep: string } | null> = {
  cabina: { question: '¿Seguís charlando o volvés a la nave?', keep: 'Seguir charlando' },
  antena: { question: '¿Seguís escuchando o volvés a la nave?', keep: 'Seguir escuchando' },
  rumbo: { question: '¿Seguís con la lección o volvés a la nave?', keep: 'Seguir con la lección' },
  tren: { question: '¿Seguís en la escena o volvés a la nave?', keep: 'Seguir en la escena' },
  bodega: {
    question: 'Ahora decilas en una frase para empezar a usarlas. O seguí guardando, o volvé a la nave.',
    keep: 'Seguir guardando',
  },
  motores: { question: '¿Seguís jugando o volvés a la nave?', keep: 'Seguir jugando' },
  radar: null,
};

/** Cuánto de la pieza está hecho (0..1): para el anillo. */
function fraction(p: PieceState, live?: PieceLive): number {
  if (p.done) return 1;
  if (live && live.need > 0) return Math.min(1, live.have / live.need);
  const main = p.need > 0 ? p.have / p.need : 0;
  if (p.said) return (main + p.said.have / p.said.need) / 2;
  return main;
}

/**
 * «Armá tu nave» en la pantalla de una actividad (encargo 117, pedido de Emmanuel del 19/10): una
 * barra dentro de la pantalla —el mismo patrón que el seguimiento de objetivos de la charla, no un
 * cartel flotante— con la pieza y cuánto falta, en vivo; al completarse, «¡Pieza lista!» con el
 * cohete y «Volver a la nave». Sólo con el recorrido en curso y si la pieza faltaba al entrar (o se
 * ganó en esta visita, aunque la pantalla haya pasado a otra pieza).
 */
export function ShipPieceTracker({
  piece: currentPiece,
  live,
  note,
  ask,
}: {
  piece: ShipPieceId | null;
  live?: PieceLive | undefined;
  /** Qué hacer en ESTA pantalla, en lugar de cuánto falta (p. ej. el Programa: qué tocar). */
  note?: string | undefined;
  /** Preguntar «¿seguís o volvés a la nave?» al completarse (sin esto, sólo la barra). */
  ask?: ShipAsk | undefined;
}) {
  // El reproductor de la Audioteca (la app lo monta arriba de todo; una pantalla aislada en un test, no).
  const listen = useContext(ListenContext);
  const { ship, act, refresh } = useShip();
  const navigate = useViewNavigate();
  const { play } = useSfx();
  const active = ship?.status === 'active';
  const current = currentPiece ? ship?.pieces.find((p) => p.id === currentPiece) : undefined;
  // Si la pieza ya estaba hecha al entrar, no hay nada que mostrar. Por pieza: la charla puede pasar
  // de una a otra (la libre que se vuelve juego son la Cabina y después los Motores).
  const [missingAtEntry, setMissingAtEntry] = useState<Partial<Record<ShipPieceId, boolean>>>({});
  useEffect(() => {
    if (currentPiece && current && missingAtEntry[currentPiece] === undefined)
      setMissingAtEntry((m) => ({ ...m, [currentPiece]: !current.done }));
  }, [currentPiece, missingAtEntry, current]);

  // La pieza ganada en esta visita sigue a la vista aunque la pantalla pase a otra (card #577: los
  // Motores, «Seguir jugando» y después «Salir» del juego dejaban la charla —ya la Cabina, hecha de
  // antes— sin «Volver a la nave»). Si la nueva también falta, se ve la nueva y el botón queda.
  const [earned, setEarned] = useState<ShipPieceId | null>(null);
  const currentShows = Boolean(currentPiece && current && missingAtEntry[currentPiece]);
  const piece = currentShows ? currentPiece : earned;
  const state = piece ? ship?.pieces.find((p) => p.id === piece) : undefined;
  const show = Boolean(piece && active && state);
  const done = state?.done === true;
  useEffect(() => {
    if (show && done && piece && currentShows) setEarned(piece);
  }, [show, done, piece, currentShows]);
  const backToo = earned !== null && earned !== piece;

  // La Antena avanza segundo a segundo con lo escuchado que todavía no llegó al servidor; lo que
  // falta es lo que llegue primero: los dos minutos o el final de un capítulo corto.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (piece !== 'antena') return;
    return subscribeListen(() => setTick((n) => n + 1));
  }, [piece]);
  // El anillo avanza en minutos de audio; el texto dice cuánto va a tardar de verdad, en reloj a la
  // velocidad actual (a 1,5× los dos minutos de audio son 1:20).
  const antenaLeft =
    piece === 'antena' && state && !state.done ? antenaRemaining(state.have, state.need) : null;
  const liveNow: PieceLive | undefined =
    piece === 'antena' && state && !state.done
      ? { have: Math.min(state.need, state.have + pendingListenSeconds()), need: state.need }
      : live;
  // Lo que la pantalla ya ve cumplido y el servidor todavía no sumó (la misión de la escena, el
  // final de lo escuchado): «Sumando la pieza…», y se pregunta seguido para que llegue al instante.
  const settling =
    !done &&
    (antenaLeft !== null
      ? antenaLeft < 0.5
      : liveNow !== undefined && liveNow.need > 0 && liveNow.have >= liveNow.need);

  // El Radar se arma al TERMINAR el recorrido por las pestañas (Emmanuel, 20/10), no al entrar. Si lo
  // cierra antes, la barra ofrece volver a verlo.
  const [tourRun, setTourRun] = useState(0);
  const [tourOpen, setTourOpen] = useState(true);
  const onTourEnd = useCallback(
    (finished: boolean) => {
      setTourOpen(false);
      if (finished) void act('radar').catch((err: unknown) => console.warn('[ship] radar', err));
    },
    [act],
  );

  // En vivo: se pregunta cada pocos segundos mientras falta (cada uno, si ya está por sumarse).
  useEffect(() => {
    if (!show || done) return;
    const t = window.setInterval(() => void refresh(), settling ? FAST_POLL_MS : POLL_MS);
    return () => window.clearInterval(t);
  }, [show, done, settling, refresh]);

  const back = () => {
    setAsking(false);
    // Con la Antena lista, volver a la nave pausa el audio (Emmanuel, 20/10).
    if (piece === 'antena') listen?.pause();
    if (ask?.onBack) ask.onBack();
    else void navigate('/home');
  };

  // La pregunta: una vez por pieza, cuando se completó en esta visita y es buen momento. Mientras
  // pregunta, la Antena se pausa (y sigue si elige seguir).
  const [asking, setAsking] = useState(false);
  const asked = useRef(new Set<ShipPieceId>());
  const pausedForAsk = useRef(false);
  useEffect(() => {
    if (!show || !done || !piece || !ask?.ready || !SHIP_ASK_TEXT[piece]) return;
    if (asked.current.has(piece)) return;
    asked.current.add(piece);
    if (piece === 'antena' && listen?.playing) {
      listen.pause();
      pausedForAsk.current = true;
    }
    setAsking(true);
  }, [show, done, piece, ask?.ready, listen]);
  // Los avisos de primera vez de la charla esperan mientras pregunta.
  useEffect(() => {
    setShipAskOpen(asking);
    return () => setShipAskOpen(false);
  }, [asking]);
  const keep = () => {
    setAsking(false);
    if (pausedForAsk.current) {
      pausedForAsk.current = false;
      listen?.play();
    }
  };

  // Al completarse, el sonido de la pieza ganada (una vez).
  const celebrated = useRef(new Set<ShipPieceId>());
  useEffect(() => {
    if (!show || !done || !piece || celebrated.current.has(piece)) return;
    celebrated.current.add(piece);
    play('ship-win');
  }, [show, done, piece, play]);

  if (!show || !state || !piece) return null;
  const p = pieceById(piece);
  const f = fraction(state, liveNow);
  const R = 9;
  const arc = 2 * Math.PI * R;

  const askText = SHIP_ASK_TEXT[piece];
  const dialog =
    asking && askText
      ? createPortal(
          <div className="sp-ask" role="dialog" aria-modal="true" aria-labelledby="sp-ask-t">
            <div className="sp-ask-card">
              <span className={`sp-ask-ic ship-c-${p.medium}`}>
                <span dangerouslySetInnerHTML={{ __html: iconSvg('check') }} />
              </span>
              <div className="sp-ask-k">¡Pieza lista!</div>
              <div className="sp-ask-t" id="sp-ask-t">
                {p.name}, en tu nave
              </div>
              <p className="sp-ask-q">{askText.question}</p>
              <div className="sp-ask-acts">
                {ask?.extra ? (
                  <>
                    <button
                      type="button"
                      className="sp-ask-back sp-ask-extra"
                      onClick={() => {
                        setAsking(false);
                        ask.extra?.onClick();
                      }}
                    >
                      {ask.extra.label}
                    </button>
                    <button type="button" className="sp-ask-keep" onClick={back}>
                      <span dangerouslySetInnerHTML={{ __html: iconSvg('rocket') }} />
                      Volver a la nave
                    </button>
                    <button type="button" className="sp-ask-link" onClick={keep}>
                      {askText.keep}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="sp-ask-back" onClick={back}>
                      <span dangerouslySetInnerHTML={{ __html: iconSvg('rocket') }} />
                      Volver a la nave
                    </button>
                    <button type="button" className="sp-ask-keep" onClick={keep}>
                      {askText.keep}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>,
          // Dentro del marco de la app: ahí viven los tokens de color (`tokens.css`); en <body> no llegan.
          document.querySelector('.app-shell__frame') ?? document.body,
        )
      : null;

  return (
    <div className={`sp-wrap${done ? ' is-done' : ''}`} role="status" aria-live="polite">
      {dialog}
      {/* El Radar: el recorrido corto por las pestañas de Progreso; terminarlo arma la pieza. */}
      {piece === 'radar' && !done && tourOpen && <ProgressTour key={tourRun} onEnd={onTourEnd} />}
      <div className="sp-bar">
        <span className={`sp-ic ship-c-${p.medium}`}>
          <svg viewBox="0 0 24 24" className="sp-ring" aria-hidden="true">
            <circle cx="12" cy="12" r={R} className="sp-ring-bg" />
            <circle
              cx="12"
              cy="12"
              r={R}
              className="sp-ring-fg"
              strokeDasharray={`${(f * arc).toFixed(1)} ${arc.toFixed(1)}`}
              transform="rotate(-90 12 12)"
            />
          </svg>
          <span
            className="sp-ic-glyph"
            dangerouslySetInnerHTML={{ __html: iconSvg(done ? 'check' : p.medium) }}
          />
        </span>
        <span className="sp-tx">
          <span className="sp-k">{done ? '¡Pieza lista!' : `Tu nave · ${p.short}`}</span>
          <span className="sp-v">
            {done
              ? `${p.name}, en tu nave`
              : settling
                ? piece === 'tren'
                  ? '¡Misión cumplida! Sumando la pieza…'
                  : 'Sumando la pieza…'
                : (note ??
                  (antenaLeft !== null
                    ? `Te falta ${clock(antenaLeft)} de escucha`
                    : pieceRemaining(state, liveNow)))}
          </span>
        </span>
        {(done || backToo) && (
          <button type="button" className="sp-back" onClick={back}>
            <span dangerouslySetInnerHTML={{ __html: iconSvg('rocket') }} />
            Volver a la nave
          </button>
        )}
        {piece === 'radar' && !done && !tourOpen && (
          <button
            type="button"
            className="sp-back is-ghost"
            onClick={() => {
              setTourRun((n) => n + 1);
              setTourOpen(true);
            }}
          >
            Ver el recorrido
          </button>
        )}
      </div>
    </div>
  );
}
